import { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API, AuthContext } from "../App";

export const TranslationContext = createContext({
  settings: { preferred_language: "en", auto_translate: true, show_original: true },
  translateText: async () => {},
  isTranslating: false,
});

export const TranslationProvider = ({ children }) => {
  const { token } = useContext(AuthContext);
  const [settings, setSettings] = useState({
    preferred_language: "en",
    auto_translate: true,
    show_original: true
  });
  const [isTranslating, setIsTranslating] = useState(false);
  const [translationCache, setTranslationCache] = useState({});

  // Fetch language settings on mount
  useEffect(() => {
    if (token) {
      fetchSettings();
    }
  }, [token]);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/users/language-settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSettings({
        preferred_language: response.data.preferred_language || "en",
        auto_translate: response.data.auto_translate !== false,
        show_original: response.data.show_original !== false
      });
    } catch (error) {
      console.error("Failed to fetch language settings:", error);
    }
  };

  const updateSettings = (newSettings) => {
    setSettings(newSettings);
  };

  // Translate a single text
  const translateText = useCallback(async (text, targetLang = null) => {
    const target = targetLang || settings.preferred_language;
    
    // Skip if auto-translate is off or target is English and text looks English
    if (!settings.auto_translate) {
      return { translated: text, original: text, wasTranslated: false };
    }

    // Check cache
    const cacheKey = `${text}_${target}`;
    if (translationCache[cacheKey]) {
      return translationCache[cacheKey];
    }

    // Skip very short texts or texts that look like they're already in target language
    if (text.length < 3) {
      return { translated: text, original: text, wasTranslated: false };
    }

    try {
      setIsTranslating(true);
      const response = await axios.post(`${API}/translate`, {
        text,
        target_language: target
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const result = {
        translated: response.data.translated_text,
        original: text,
        wasTranslated: response.data.translated_text !== text && !response.data.translation_failed,
        detectedLanguage: response.data.source_language
      };

      // Cache the result
      setTranslationCache(prev => ({ ...prev, [cacheKey]: result }));

      return result;
    } catch (error) {
      console.error("Translation error:", error);
      return { translated: text, original: text, wasTranslated: false };
    } finally {
      setIsTranslating(false);
    }
  }, [token, settings.preferred_language, settings.auto_translate, translationCache]);

  // Translate multiple texts at once
  const translateBatch = useCallback(async (texts, targetLang = null) => {
    const target = targetLang || settings.preferred_language;
    
    if (!settings.auto_translate || texts.length === 0) {
      return texts.map(t => ({ translated: t, original: t, wasTranslated: false }));
    }

    try {
      const response = await axios.post(`${API}/translate/batch`, {
        texts,
        target_language: target
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      return response.data.translations.map(t => ({
        translated: t.translated,
        original: t.original,
        wasTranslated: t.translated !== t.original
      }));
    } catch (error) {
      console.error("Batch translation error:", error);
      return texts.map(t => ({ translated: t, original: t, wasTranslated: false }));
    }
  }, [token, settings.preferred_language, settings.auto_translate]);

  return (
    <TranslationContext.Provider value={{
      settings,
      updateSettings,
      translateText,
      translateBatch,
      isTranslating,
      refreshSettings: fetchSettings
    }}>
      {children}
    </TranslationContext.Provider>
  );
};

export const useTranslation = () => useContext(TranslationContext);
